import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Phone, PhoneOff, Mic, MicOff, Loader2 } from "lucide-react";
import { API, getAuthHeaders } from "@/App";

/**
 * VoiceCallDialog
 * props:
 *  - open, onOpenChange
 *  - mode: "outgoing" | "incoming"
 *  - peer: { user_id, name, picture }
 *  - callId: string
 *  - wsRef: ref to the open WebSocket (for sending signals)
 *  - incomingOffer: { sdp } — only for incoming
 *  - selfUserId
 */
export default function VoiceCallDialog({ open, onOpenChange, mode, peer, callId, wsRef, incomingOffer, selfUser }) {
  const [status, setStatus] = useState(mode === "outgoing" ? "ringing" : "incoming"); // ringing | in_call | ended | incoming | connecting
  const [muted, setMuted] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const pendingIceRef = useRef([]);
  const startedAtRef = useRef(null);
  const timerRef = useRef(null);
  const endedRef = useRef(false);

  const send = (payload) => {
    try {
      const ws = wsRef?.current;
      if (ws && ws.readyState === 1) ws.send(JSON.stringify(payload));
    } catch (e) { /* noop */ }
  };

  const cleanup = () => {
    try { localStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    try { pcRef.current?.close(); } catch {}
    pcRef.current = null;
    localStreamRef.current = null;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const fetchIce = async () => {
    try {
      const res = await fetch(`${API}/calls/ice-servers`, { headers: getAuthHeaders(), credentials: "include" });
      if (res.ok) { const d = await res.json(); return d.iceServers || []; }
    } catch {}
    return [{ urls: ["stun:stun.l.google.com:19302"] }];
  };

  const buildPC = async () => {
    const iceServers = await fetchIce();
    const pc = new RTCPeerConnection({ iceServers });
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        send({ type: "call_ice", to_user_id: peer.user_id, call_id: callId, candidate: e.candidate.toJSON() });
      }
    };
    pc.ontrack = (e) => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = e.streams[0];
        remoteAudioRef.current.play().catch(() => {});
      }
    };
    pc.onconnectionstatechange = () => {
      if (["failed", "disconnected", "closed"].includes(pc.connectionState)) {
        endCall("disconnected");
      } else if (pc.connectionState === "connected") {
        setStatus("in_call");
        if (!startedAtRef.current) {
          startedAtRef.current = Date.now();
          timerRef.current = setInterval(() => setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000)), 1000);
        }
      }
    };
    pcRef.current = pc;
    return pc;
  };

  const startOutgoing = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      const pc = await buildPC();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);
      send({
        type: "call_offer",
        to_user_id: peer.user_id,
        call_id: callId,
        sdp: { type: offer.type, sdp: offer.sdp },
        from_user: selfUser || { user_id: undefined, name: "Usuario" },
      });
      setStatus("ringing");
    } catch (e) {
      console.error("Outgoing call error:", e);
      setStatus("ended");
      setTimeout(() => onOpenChange(false), 1200);
    }
  };

  const acceptIncoming = async () => {
    setStatus("connecting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      const pc = await buildPC();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      await pc.setRemoteDescription(new RTCSessionDescription(incomingOffer));
      // Drain any ICE that arrived before remote desc
      for (const c of pendingIceRef.current) { try { await pc.addIceCandidate(c); } catch {} }
      pendingIceRef.current = [];
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      send({ type: "call_answer", to_user_id: peer.user_id, call_id: callId, sdp: { type: answer.type, sdp: answer.sdp } });
    } catch (e) {
      console.error("Accept call error:", e);
      endCall("failed");
    }
  };

  const rejectIncoming = () => {
    send({ type: "call_reject", to_user_id: peer.user_id, call_id: callId });
    setStatus("ended");
    cleanup();
    onOpenChange(false);
  };

  const endCall = (reason = "hangup", { silent = false } = {}) => {
    if (!endedRef.current && !silent) {
      send({ type: "call_end", to_user_id: peer.user_id, call_id: callId, reason });
    }
    endedRef.current = true;
    setStatus("ended");
    cleanup();
    setTimeout(() => onOpenChange(false), 600);
  };

  // Listen for signaling messages on the shared WS
  useEffect(() => {
    if (!open) return;
    const ws = wsRef?.current;
    if (!ws) return;
    const handler = async (event) => {
      let data; try { data = JSON.parse(event.data); } catch { return; }
      if (!data || data.call_id !== callId) return;
      const pc = pcRef.current;
      if (data.type === "call_answer" && pc) {
        try {
          await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
          // Drain any ICE candidates that arrived before the remote answer was set.
          for (const c of pendingIceRef.current) {
            try { await pc.addIceCandidate(c); } catch (e) { /* noop */ }
          }
          pendingIceRef.current = [];
        } catch (e) { console.warn(e); }
      } else if (data.type === "call_ice") {
        const cand = new RTCIceCandidate(data.candidate);
        if (pc && pc.remoteDescription) { try { await pc.addIceCandidate(cand); } catch (e) {} }
        else { pendingIceRef.current.push(cand); }
      } else if (data.type === "call_end" || data.type === "call_reject") {
        // Remote already terminated — do NOT echo a call_end back to them.
        endCall(data.type === "call_reject" ? "rejected" : "remote_hangup", { silent: true });
      }
    };
    ws.addEventListener("message", handler);
    return () => { try { ws.removeEventListener("message", handler); } catch {} };
    // eslint-disable-next-line
  }, [open, callId, wsRef]);

  // Kick off outgoing flow when dialog opens
  useEffect(() => {
    if (!open) return;
    if (mode === "outgoing") startOutgoing();
    return () => cleanup();
    // eslint-disable-next-line
  }, [open, mode]);

  const toggleMute = () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !muted;
    stream.getAudioTracks().forEach((t) => (t.enabled = !next));
    setMuted(next);
  };

  const fmtDur = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) endCall("dialog_closed"); onOpenChange(v); }}>
      <DialogContent className="bg-[#0e1621] text-white border-[#17212b] max-w-sm">
        <DialogHeader><DialogTitle className="text-center text-base text-gray-300">Llamada de voz</DialogTitle></DialogHeader>
        <div className="flex flex-col items-center py-4 gap-3">
          <Avatar className="w-24 h-24">
            <AvatarImage src={peer?.picture} />
            <AvatarFallback className="bg-[#2b5278] text-white text-2xl">{peer?.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
          </Avatar>
          <div className="text-lg font-semibold">{peer?.name || "Usuario"}</div>
          <div className="text-sm text-gray-400 min-h-[1.25rem]">
            {status === "ringing" && (mode === "outgoing" ? "Llamando..." : "Llamada entrante")}
            {status === "incoming" && "Llamada entrante"}
            {status === "connecting" && (<span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Conectando...</span>)}
            {status === "in_call" && fmtDur(seconds)}
            {status === "ended" && "Llamada finalizada"}
          </div>
          <audio ref={remoteAudioRef} autoPlay playsInline />
          <div className="flex items-center gap-3 mt-4">
            {status === "incoming" || (status === "ringing" && mode === "incoming") ? (
              <>
                <Button onClick={rejectIncoming} className="bg-red-600 hover:bg-red-700 rounded-full w-14 h-14"><PhoneOff className="w-6 h-6" /></Button>
                <Button onClick={acceptIncoming} className="bg-green-600 hover:bg-green-700 rounded-full w-14 h-14"><Phone className="w-6 h-6" /></Button>
              </>
            ) : status !== "ended" ? (
              <>
                <Button onClick={toggleMute} className={`${muted ? "bg-red-600 hover:bg-red-700" : "bg-[#2b3a4a] hover:bg-[#34465a]"} rounded-full w-14 h-14`}>
                  {muted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                </Button>
                <Button onClick={() => endCall("hangup")} className="bg-red-600 hover:bg-red-700 rounded-full w-14 h-14"><PhoneOff className="w-6 h-6" /></Button>
              </>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
